import { str } from "../strings"
import { LocaleString } from "../types"
import { isArray, isDate, isNumber, isObject, isString } from "./utils"

Date.isLeapYear = function(year) {
    return (((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0));
};

Date.getDaysInMonth = function(year, month) {
    return [31, (Date.isLeapYear(year) ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
};

Date.prototype.isLeapYear = function() {
    return Date.isLeapYear(this.getFullYear());
};

Date.prototype.getDaysInMonth = function() {
    return Date.getDaysInMonth(this.getFullYear(), this.getMonth());
};

Date.prototype.addMonths = function(value) {
    const n = this.getDate();
    const d = new Date(this.valueOf());
    d.setDate(1);
    d.setMonth(this.getMonth() + value);
    d.setDate(Math.min(n, this.getDaysInMonth(this.getFullYear(), this.getMonth())));
    return d;
};

Date.prototype.addDays = function(days: number) {
    const d = new Date(this.valueOf());
    d.setDate(d.getDate() + days);
    return d;
}

/**
 * True if two date intervals have an intersection
 * @param start1 Interval 1 start
 * @param end1 Interval 1 end
 * @param start2 Interval 2 start
 * @param end2 Interval 2 end
 * @param allowSameDay If true, the two date intervals can share a single day at their limit
 */
export function checkDateIntersection(start1: Date, end1: Date, start2: Date, end2: Date, allowSameDay: boolean = false): boolean {

    const check = () => {

        const check = (a: Date, b: Date, d: Date) => (d >= a) && (d <= b)

        if (check(start1, end1, start2)) return true
        if (check(start1, end1, end2)) return true
        if (check(start2, end2, start1)) return true

        return false

    }

    let hasIntersection = check()

    if (hasIntersection && allowSameDay) {

        const getDate = (...dates: Date[]) => dates.map(d => toDate(d, true)?.valueOf())

        const [s1, e1, s2, e2] = getDate(start1, end1, start2, end2)

        if ((s2 == e1) || (s1 == e2)) {
            hasIntersection = false
        }

    }

    return hasIntersection

}

export function getDateIntersection(start1: Date, end1: Date, start2: Date, end2: Date): { start: Date, end: Date } {

    var vs1 = start1.valueOf();
    var ve1 = end1.valueOf();
    var vs2 = start2.valueOf();
    var ve2 = end2.valueOf();

    var start = (vs1 >= vs2) ? vs1 : vs2;
    var end = (ve1 <= ve2) ? ve1 : ve2;

    if (start > end) return null;

    return {
        start: new Date(start),
        end: new Date(end),
    };

}

export function toDate(value: any, clearTime = false) {

    let date: Date = undefined

    if (isNumber(value)) {
        date = new Date(<number>value);
    }
    else if (isString(value)) {
        date = new Date(<string>value);
    }
    else if (isDate(value)) {
        date = value
    }

    if (isDate(date)) {

        if (clearTime) {
            date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        }
    }

    return date

}



export function parseDate(value: string): Date {
    const dateRegex = new RegExp("^\\s*([0-3]\\d)/([0-1]\\d)/(\\d{4})\\s*$")
    const match = dateRegex.exec(value)
    if (!match) {
        return
    }

    const day = (Number)(match[1])
    const month = (Number)(match[2])
    const year = (Number)(match[3])

    return new Date(year, month - 1, day)
}

export function setDates<T>(items: T[], properties: string[], clearTime: boolean): T[] {

    if (items) {

        items.filter(i => !!i).forEach(i => {

            properties.forEach(p => {

                i[p] = toDate(i[p], clearTime)

            });

        });

    }

    return items;

}

export function dateBetweenWithoutYear(startDate: Date, endDate: Date, sourceDate: Date) {
    if (!sourceDate) {
        return false;
    }

    var start = new Date(startDate.getTime());
    start.setHours(0, 0, 0);
    start.setFullYear(2000);
    var end = new Date(endDate.getTime());
    end.setHours(0, 0, 0);
    end.setFullYear(endDate.getMonth() < startDate.getMonth() ? 2001 : 2000);
    var date = new Date(sourceDate.getTime());
    date.setHours(0, 0, 0);
    date.setFullYear(sourceDate.getMonth() < startDate.getMonth() ? 2001 : 2000);

    return start <= date && end >= date;
}

export function dateCompareWithoutYear(date1: Date, date2: Date, limit: number): number {

    var d1 = new Date(date1.getTime());
    d1.setFullYear(date1.getMonth() < limit ? 2001 : 2000);
    var d2 = new Date(date2.getTime());
    d2.setFullYear(date2.getMonth() < limit ? 2001 : 2000);

    return d1 > d2 ? 1 : d1 < d2 ? -1 : 0;
}

/**
 * Converts any Date property on then input parameter to a JSON string with no time part
 * The input parameter can be an array or an object - in which case, the input is changed
 * in place
 */
export function convertDatesToJsonUtc(item: any) {

    if (isDate(item)) {
        // item + " UTC" does not work on iOS, so we rely on getTimezoneOffset() to remove the offset
        const dateUTC = new Date(item.getTime() + item.getTimezoneOffset() * -60000);
        //var dateUTC = new Date(item + " UTC");
        return (dateUTC).toISOString().slice(0, -14);
    }

    else if (isArray(item)) {

        item.forEach(i => convertDatesToJsonUtc(i));

    }

    else if (isObject(item)) {

        Object.keys(item).forEach(k => {

            item[k] = convertDatesToJsonUtc(item[k]);

        });

    }

    return item;

}


export function getStartOfWeek(date: Date, clearTime: boolean = true): Date {

    var day = (6 + date.getDay()) % 7;

    date = date.addDays(-day);

    if (clearTime) {
        date.setUTCHours(0, 0, 0);
    }

    return date;

}

export function getDateString(date: Date): string {
    var day = date.getDate();
    var month = date.getMonth() + 1;
    var dayStr = day < 10 ? "0" + day : day;
    var monthStr = month < 10 ? "0" + month : month;
    return dayStr + "/" + monthStr + "/" + date.getFullYear();
}

export function compareDates(d1: Date, d2: Date): number {

    let f1 = isDate(d1);
    let f2 = isDate(d2);

    if (f1 != f2) {
        return f1 ? -1 : 1;
    }

    if (!f1) return 0;

    return d1.valueOf() - d2.valueOf();

}

export function getDateRangeDescr(startDate: Date | string, endDate?: Date | string) {

    return [startDate, endDate]
        .filter(d => !!d)
        .map(d => d instanceof Date ? d : new Date(d))
        .filter(d => d instanceof Date)
        .map(d => d.toLocaleDateString(undefined, { day: "2-digit", "month": "2-digit", year: "2-digit" }))
        .join(" \u21E2 ")

}

// export function getISODateString(date: Date) {

//     if (!isDate(date)) {
//         return undefined
//     }

//     const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
//     return utc.toISOString().substring(0, 10)
// }

export function formatDateDayMonth(date: Date) {
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" })
}

/**
 * Returns a label describing one or more selected years ("years"), picked from a set of available years ("allYears")
 * The label is composed so as to show ranges of contiguous years as [start-end] intervals
 * For instance, if years=[2017,2018,2019,2020], then the label will be "2017-2020"
 * If there are gaps, then non-consecutive intervals are separated by commands
 * For instance, if years=[2017,2020,2021], then the label will be "2017, 2020-2021"
 * @param years Selected years
 * @param allYears All available years
 * @returns Label
 */
export function getSelectedYearsLabel(years: number[], allYears: number[]) {

    if (allYears && allYears.every(y1 => years.some(y2 => y2 == y1))) {
        return "All years" as LocaleString
    }

    if (!years.length) {
        return str.NoYear
    }

    const s = years.sort((y1, y2) => y2 - y1)

    const groups = s.reduce((ranges, y) => {

        const prev = ranges.length ? ranges[ranges.length - 1] : null

        if (prev && (prev.last == (y + 1))) {
            prev.last = y
        }
        else {
            ranges.push({
                first: y,
                last: y
            })
        }

        return ranges

    }, []);

    // "\u2011" is a hyphen
    const labels = groups.map(g => `${g.first}${(g.first > g.last ? "\u2011" + String(g.last) : "")}`)

    return labels.join(", ")

}

/**
 * Formats the "dateLike" date as an ISO 8601 date, assuming that this date is in the "timeZone" time zone
 *
 * Note that this does not convert the date to the specified time zone, it only assumes that is this is the time zone it is expressed in
 *
 * The goal is to be able to convert to ISO 8601 without relying on the built-in .toISOString() DateTime method, which assumes
 * that the date is expressed in the "current" time zone
 *
 * @param dateLike Date to format
 * @param timeZone Time zone this date is in
 * @param omitTime If true, the time part will be omitted (i.e., set to 00:00:00)
 * @returns Date in ISO 8601 format
 */
export function formatDateToISOString(dateLike: Date | number | string, timeZone: string, omitTime = false) {

    if (!dateLike) {
        return undefined
    }

    let date = toDate(dateLike, omitTime)

    if (!timeZone) {
        return date.toISOString()
    }

    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (currentTimeZone === timeZone) {
        return date.toISOString()
    }

    function getParts(timeZone: string) {

        const formatter = new Intl.DateTimeFormat(
            'en-US',
            {
                timeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            })

        const parts = formatter.formatToParts(date)


        const dateParts: any = {}

        for (const { type, value } of parts) {
            dateParts[type] = value
        }

        return {
            hour: Number.parseInt(dateParts.hour),
            minute: Number.parseInt(dateParts.minute)
        }

    }

    function pz(arg: number) {
        return String(arg).padStart(2, '0')
    }

    const target = getParts(timeZone)
    const utc = getParts("UTC")

    const hours = date.getHours() + (utc.hour - target.hour)
    const minutes = date.getMinutes() + (utc.minute - target.minute)

    date = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        hours,
        minutes,
        date.getSeconds(),
        date.getMilliseconds()
    )

    const dateString = `${date.getFullYear()}-${pz(date.getMonth() + 1)}-${pz(date.getDate())}T${pz(date.getHours())}:${pz(date.getMinutes())}:${pz(date.getSeconds())}Z`

    return dateString

}

/**
 * Formats date to the given locale (en-GB by default).
 * @param value the value to format
 * @param locale the locale, by default en-GB
 * @param options options, by default 2digit day, 2digit month, 4digit year
 * @param fallback returned when the value is not a valid date (empty string by default)
 * @returns formatted string e.g. 23/12/2024
 */
export const formatDateToLocale = (
    value: Date | string | number,
    locale: string = 'en-GB',
    options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
    fallback: string = ''
): string => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return fallback;

    return new Intl.DateTimeFormat(locale, options).format(date);
}
